import type { GatewayBrowserClient } from "../gateway.ts";

export type GoogleDriveHost = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  googleDriveStatus: {
    configured: boolean;
    clientEmail?: string;
    storagePath?: string;
    viaAccessTokenEnv?: boolean;
  } | null;
  googleDriveBusy: boolean;
  googleDriveMessage: string | null;
  googleDriveJsonDraft: string;
};

export async function loadGoogleDriveStatus(host: GoogleDriveHost): Promise<void> {
  if (!host.client || !host.connected) {
    host.googleDriveStatus = null;
    return;
  }
  try {
    host.googleDriveStatus = await host.client.request<{
      configured: boolean;
      clientEmail?: string;
      storagePath?: string;
      viaAccessTokenEnv?: boolean;
    }>("googleDrive.status", {});
  } catch {
    host.googleDriveStatus = null;
  }
}

export async function saveGoogleDriveServiceAccount(host: GoogleDriveHost): Promise<void> {
  if (!host.client || !host.connected) {
    host.googleDriveMessage = "Connect to the gateway first.";
    return;
  }
  const raw = host.googleDriveJsonDraft.trim();
  if (!raw) {
    host.googleDriveMessage = "Paste the service account JSON first.";
    return;
  }
  host.googleDriveBusy = true;
  host.googleDriveMessage = null;
  try {
    await host.client.request<{ ok: boolean; clientEmail?: string }>(
      "googleDrive.serviceAccount.set",
      { json: raw },
    );
    host.googleDriveJsonDraft = "";
    host.googleDriveMessage =
      "Saved. Share Drive folders with this service account email if files do not appear.";
    await loadGoogleDriveStatus(host);
  } catch (err) {
    host.googleDriveMessage = err instanceof Error ? err.message : String(err);
  } finally {
    host.googleDriveBusy = false;
  }
}

export async function clearGoogleDriveServiceAccount(host: GoogleDriveHost): Promise<void> {
  if (!host.client || !host.connected) {
    return;
  }
  host.googleDriveBusy = true;
  host.googleDriveMessage = null;
  try {
    await host.client.request("googleDrive.serviceAccount.clear", {});
    host.googleDriveMessage = "Removed on-disk service account key.";
    await loadGoogleDriveStatus(host);
  } catch (err) {
    host.googleDriveMessage = err instanceof Error ? err.message : String(err);
  } finally {
    host.googleDriveBusy = false;
  }
}

export async function testGoogleDriveConnection(host: GoogleDriveHost): Promise<void> {
  if (!host.client || !host.connected) {
    host.googleDriveMessage = "Connect to the gateway first.";
    return;
  }
  host.googleDriveBusy = true;
  host.googleDriveMessage = null;
  try {
    const result = await host.client.request<{ ok?: boolean; about?: unknown }>(
      "googleDrive.test",
      {},
    );
    host.googleDriveMessage = `OK — Drive API reachable. ${JSON.stringify(result?.about ?? result)}`;
  } catch (err) {
    host.googleDriveMessage = err instanceof Error ? err.message : String(err);
  } finally {
    host.googleDriveBusy = false;
  }
}
